import { Icon, IconNames } from '@dynamic/ui';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import { focusShadow } from '#/components/styles';
import { useActions } from '#/hooks/actions/useActions';
import { useIsMobile } from '#/hooks/useResponsiveValue';
import { CoreFooterFeature, SocialKey } from '#/service/config/features.types';
import { remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

interface StyledProps {
  $isMobile?: boolean;
}

const InfoContainer = styled.div<StyledProps>`
  font: var(--fontStyleFootnote);
  background-color: var(--pageSecondaryBackground);
  padding: ${remify(15)} ${remify(50)};
  display: flex;
  flex-direction: ${({ $isMobile }) => ($isMobile ? 'column' : 'row')};
  justify-content: center;
`;

const Copyright = styled.div<StyledProps>`
  text-align: left;
  justify-content: flex-start;
  flex: 1;

  ${({ $isMobile }) =>
    $isMobile
      ? css`
          text-align: center;
          padding: ${remify(5)} 0 ${remify(5)} 0;
        `
      : css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `}
`;

const Socials = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
`;

const SocialItem = styled.button`
  margin: 0 ${remify(7.5)};
  background: none;
  border: none;
  color: inherit;
  outline: none;
  padding: 0;
  line-height: 100%;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.2);
  }

  &:focus-visible {
    ${focusShadow}
  }
`;

const Contact = styled.div<StyledProps>`
  text-align: right;
  justify-content: flex-end;
  flex: 1;

  ${({ $isMobile }) =>
    $isMobile
      ? css`
          text-align: center;
        `
      : css`
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `}
`;

const socialKeyToIcon: Record<SocialKey, IconNames> = {
  [SocialKey.FACEBOOK]: 'facebook',
  [SocialKey.INSTAGRAM]: 'instagram',
  [SocialKey.LINKED_IN]: 'linkedin',
  [SocialKey.TWITTER]: 'twitter',
  [SocialKey.YOUTUBE]: 'youtube',
  [SocialKey.TIKTOK]: 'tiktok',
  [SocialKey.PINTEREST]: 'pinterest',
};

export const Info: FC<Omit<CoreFooterFeature, 'sections'>> = ({
  copyright,
  contact,
  socials,
}) => {
  const { handleAction } = useActions();
  const isMobile = useIsMobile();

  const parsedCopyright = tb(copyright, {
    currentYear: new Date().getFullYear(),
  });

  return (
    <InfoContainer $isMobile={isMobile}>
      {!isMobile && (
        <Copyright $isMobile={isMobile}>{parsedCopyright}</Copyright>
      )}
      <Socials>
        {socials.map(({ key, action }) => (
          <SocialItem
            aria-label={tb(`tts.socials.${key}`)}
            key={key}
            onClick={() => handleAction(action)}
          >
            <Icon key={key} name={socialKeyToIcon[key] ?? 'link'} />
          </SocialItem>
        ))}
      </Socials>
      {isMobile && (
        <Copyright $isMobile={isMobile}>{parsedCopyright}</Copyright>
      )}
      <Contact $isMobile={isMobile}>{tb(contact)}</Contact>
    </InfoContainer>
  );
};
